import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Button,
  Stack,
  TextField,
  InputAdornment,
  CardActionArea,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Inventory,
  Warning,
  Error,
  CheckCircle,
  ShoppingCart,
  AttachMoney,
  Visibility,
  Edit,
  Search,
  Circle,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { useNavigate } from 'react-router-dom';
import { fetchProductStatsAsync } from '../store/productSlice';
import { stockStatusColors } from '../theme/theme';
import api from '../services/api';
import type { Profitability } from '../components/profitability/types';
import ProfitSummary from '../components/profitability/ProfitSummary';
import type { Product as ProductType, Recipe, SalesForecastResponse, YoYComparisonResponse, QuarterlyBreakdownResponse, SalesByCustomerTypeResponse, StrategicClientsReportResponse, Customer } from '../types';
import { TrafficLight, BlockMeter } from '../components/charts/SimpleCharts';
import { ProjectionChart } from '../components/charts/ProjectionChart';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  onClick?: () => void;
  tooltip?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend, onClick, tooltip }) => (
  <Card sx={{ height: '100%' }}>
    <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title={tooltip ?? title} arrow placement="top">
          <Typography variant="body2" color="text.secondary" noWrap>{title}</Typography>
        </Tooltip>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 48 }}>
          <Avatar sx={{ bgcolor: color, width: 28, height: 28 }}>{icon}</Avatar>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }}>{value}</Typography>
        </Stack>
        {trend && (
          <Stack direction="row" spacing={1} alignItems="center">
            {trend.isPositive ? (
              <TrendingUp color="success" fontSize="small" />
            ) : (
              <TrendingDown color="error" fontSize="small" />
            )}
            <Typography variant="caption" color={trend.isPositive ? 'success.main' : 'error.main'}>{trend.value}% vs mes anterior</Typography>
          </Stack>
        )}
      </CardContent>
    </CardActionArea>
  </Card>
);

interface ProductListCardProps {
  title: string;
  products: any[];
  emptyMessage: string;
  showStock?: boolean;
  onViewProduct?: (id: number) => void;
  onEditProduct?: (id: number) => void;
}

const ProductListCard: React.FC<ProductListCardProps> = ({
  title,
  products,
  emptyMessage,
  showStock = true,
  onViewProduct,
  onEditProduct,
}) => (
  <Card sx={{ height: 400 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom fontWeight="600">
        {title}
      </Typography>
      {products.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <List sx={{ maxHeight: 320, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          {products.map((product) => (
            <ListItem
              key={product.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:last-child': { mb: 0 },
              }}
              secondaryAction={
                <Box>
                  {onViewProduct && (
                    <Tooltip title="Ver producto">
                      <IconButton size="small" onClick={() => onViewProduct(product.id)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onEditProduct && (
                    <Tooltip title="Editar producto">
                      <IconButton size="small" onClick={() => onEditProduct(product.id)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
            >
              <ListItemIcon>
                {showStock && (
                  <Box sx={{ position: 'relative' }}>
                    {product.stockStatus === 'CRITICAL' && <Error color="error" />}
                    {product.stockStatus === 'LOW' && <Warning color="warning" />}
                    {product.stockStatus === 'WARNING' && <Warning color="warning" />}
                    {product.stockStatus === 'OK' && <CheckCircle color="success" />}
                  </Box>
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">{product.name}</Typography>
                    {showStock && (
                      <Chip
                        label={`Stock: ${product.currentStock || 0}`}
                        size="small"
                        color={
                          product.stockStatus === 'CRITICAL' ? 'error' :
                          product.stockStatus === 'LOW' ? 'warning' :
                          product.stockStatus === 'WARNING' ? 'warning' : 'success'
                        }
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    SKU: {product.sku}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </CardContent>
  </Card>
);

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { stats, loading } = useSelector((state: RootState) => state.products);
  const { user, tenant } = useSelector((state: RootState) => state.auth);
  const [channels, setChannels] = useState<any[]>([]);
  const [purchaseCount, setPurchaseCount] = useState<number | null>(null);
  const [purchaseSuggestions, setPurchaseSuggestions] = useState<any[]>([]);
  const [kpis, setKpis] = useState<{ totalSales: number; totalOrders: number; avgOrderValue: number; salesTrendPct: number; totalUnits: number } | null>(null);
  const [series, setSeries] = useState<{ week: string; value: number }[]>([]);
  const [kpiPeriod, setKpiPeriod] = useState<'day'|'week'|'month'|'custom'>('week');
  // Rentabilidad del período (ventas netas − costo directo por receta y comisiones − gastos fijos), igual que /profitability.
  const [profit, setProfit] = useState<Profitability | null>(null);
  const [locations, setLocations] = useState<string[]>([]);
  const [topProducts, setTopProducts] = useState<ProductType[]>([]);
  const [locStocks, setLocStocks] = useState<Record<number, { location: string; stock: number }[]>>({});
  const [selectedProductForTimeline, setSelectedProductForTimeline] = useState<number | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [projectionWindow, setProjectionWindow] = useState<'1h'|'2h'|'4h'|'8h'|'day'>('day');
  const [recipesByProduct, setRecipesByProduct] = useState<Record<number, Recipe | null>>({});
  const [componentTotalStock, setComponentTotalStock] = useState<Record<string, number>>({});
  const [materialsMap, setMaterialsMap] = useState<Record<number, { currentStock: number; name: string }>>({});
  const [allProducts, setAllProducts] = useState<ProductType[]>([]);
  const [riskDays, setRiskDays] = useState<number>(3);
  const [recentMovs, setRecentMovs] = useState<any[]>([]);
  const [simpleMode, setSimpleMode] = useState<boolean>(true);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [salesByChannel, setSalesByChannel] = useState<{ channel: string; totalSales: number; totalUnits: number; totalOrders: number }[]>([]);

  // New report data
  const [salesForecast, setSalesForecast] = useState<SalesForecastResponse | null>(null);
  const [yoyComparison, setYoyComparison] = useState<YoYComparisonResponse | null>(null);
  const [quarterlyBreakdown, setQuarterlyBreakdown] = useState<QuarterlyBreakdownResponse | null>(null);
  const [salesByCustomerType, setSalesByCustomerType] = useState<SalesByCustomerTypeResponse | null>(null);
  const [strategicReport, setStrategicReport] = useState<StrategicClientsReportResponse | null>(null);
  const [strategicCustomers, setStrategicCustomers] = useState<Customer[]>([]);
  const [selectedStrategicCustomer, setSelectedStrategicCustomer] = useState<number | null>(null);

  const sectionGap = { xs: 2, md: 3 } as const;
  const kpiMinH = 120;

  useEffect(() => {
    dispatch(fetchProductStatsAsync());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const ch = await api.getChannels();
        setChannels(ch.items || []);
      } catch {}
      try {
        const sugg = await api.getPurchaseSuggestions();
        setPurchaseSuggestions(sugg.items || []);
        setPurchaseCount((sugg.items || []).length);
      } catch {}
      try {
        const prods = await api.getProducts({ page: 1, pageSize: 200 });
        setAllProducts(prods.products || []);
        if (!selectedProductForTimeline && (prods.products || []).length > 0) {
          setSelectedProductForTimeline((prods.products || [])[0].id);
        }
      } catch {}
      try {
        const mov = await api.getInventoryMovements({ page: 1, pageSize: 10 });
        setRecentMovs(mov.items || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (kpiPeriod === 'custom') {
          const params: { from?: string; to?: string } = {};
          if (customFrom) params.from = customFrom;
          if (customTo) params.to = customTo;
          const ch = await api.getSalesByChannel(params);
          setSalesByChannel(ch.items || []);
          const totalSales = (ch as any).totalSales ?? (ch.items || []).reduce((acc: number, it: any) => acc + (it.totalSales || 0), 0);
          const totalUnits = (ch as any).totalUnits ?? (ch.items || []).reduce((acc: number, it: any) => acc + (it.totalUnits || 0), 0);
          const totalOrders = (ch as any).totalOrders ?? (ch.items || []).reduce((acc: number, it: any) => acc + (it.totalOrders || 0), 0);
          const avgOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
          setKpis({
            totalSales,
            totalOrders,
            avgOrderValue,
            salesTrendPct: 0,
            totalUnits,
          });
        } else {
          const kp = await api.getSalesKpis(kpiPeriod);
          setKpis({ totalSales: kp.totalSales, totalOrders: kp.totalOrders, avgOrderValue: kp.avgOrderValue, salesTrendPct: kp.salesTrendPct, totalUnits: kp.totalUnits });
          const ch = await api.getSalesByChannel({ period: kpiPeriod });
          setSalesByChannel(ch.items || []);
        }
      } catch {}
      try {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
        const minus = (days: number) => { const d = new Date(`${today}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - days); return d.toISOString().slice(0, 10); };
        const range = kpiPeriod === 'day' ? { from: today, to: today }
          : kpiPeriod === 'week' ? { from: minus(6), to: today }
          : kpiPeriod === 'custom' ? { from: customFrom || `${today.slice(0, 7)}-01`, to: customTo || today }
          : { from: `${today.slice(0, 7)}-01`, to: today };
        setProfit(await api.getProfitability(range));
      } catch { setProfit(null); }
    })();
  }, [kpiPeriod, customFrom, customTo]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.getSalesSeries(12);
        setSeries(s.items || []);
      } catch {}
    })();
  }, []);

  // Load new report data
  useEffect(() => {
    (async () => {
      try {
        const forecast = await api.getSalesForecast(3);
        setSalesForecast(forecast);
      } catch {}
      try {
        const yoy = await api.getYoYComparison({ monthsBack: 6 });
        setYoyComparison(yoy);
      } catch {}
      try {
        const quarterly = await api.getQuarterlyBreakdown();
        setQuarterlyBreakdown(quarterly);
      } catch {}
      try {
        const byType = await api.getSalesByCustomerType();
        setSalesByCustomerType(byType);
      } catch {}
      try {
        // Load strategic customers for dropdown
        const custs = await api.getCustomers({ page: 1, pageSize: 100, type: 'B2B_Estrategico' });
        setStrategicCustomers(custs.customers || []);
      } catch {}
    })();
  }, []);

  // Load strategic client report when customer is selected
  useEffect(() => {
    if (selectedStrategicCustomer) {
      (async () => {
        try {
          const report = await api.getStrategicClientReport({ customerId: selectedStrategicCustomer });
          setStrategicReport(report);
        } catch {}
      })();
    } else {
      setStrategicReport(null);
    }
  }, [selectedStrategicCustomer]);

  // Load locations and top products for multi-warehouse stock visualization
  useEffect(() => {
    (async () => {
      try {
        const l = await api.getLocations();
        setLocations(l.locations || []);
      } catch {}
      try {
        const prods = await api.getProducts({ page: 1, pageSize: 10 });
        const top = (prods.products || []).slice(0, 6);
        setTopProducts(top);
        // Fetch per-product location stocks
        const stocks: Record<number, { location: string; stock: number }[]> = {};
        for (const p of top) {
          try {
            const s = await api.getLocationStocks(p.id);
            stocks[p.id] = s.items || [];
          } catch {}
        }
        setLocStocks(stocks);
      } catch {}
      // Preload materials for stock checks
      try {
        const mats = await api.getMaterials({ page: 1, pageSize: 500 });
        const mmap: Record<number, { currentStock: number; name: string }> = {};
        for (const m of (mats.materials || [])) mmap[m.id] = { currentStock: m.currentStock, name: m.name };
        setMaterialsMap(mmap);
      } catch {}
    })();
  }, []);

  // Load recipe for timeline when a product is selected
  useEffect(() => {
    (async () => {
      if (!selectedProductForTimeline) { setSelectedRecipe(null); return; }
      try {
        const list = await api.getRecipes({ page: 1, pageSize: 1, productId: selectedProductForTimeline });
        const rec = list.recipes?.[0];
        if (rec) setSelectedRecipe(rec);
        else setSelectedRecipe(null);
      } catch { setSelectedRecipe(null); }
    })();
  }, [selectedProductForTimeline]);

  // Load recipes for top products and their component stocks
  useEffect(() => {
    (async () => {
      if (!topProducts || topProducts.length === 0) return;
      const recs: Record<number, Recipe | null> = { ...recipesByProduct };
      const compStocks: Record<string, number> = { ...componentTotalStock };
      for (const p of topProducts) {
        if (!recs[p.id]) {
          try {
            const list = await api.getRecipes({ page: 1, pageSize: 1, productId: p.id });
            recs[p.id] = list.recipes?.[0] || null;
          } catch { recs[p.id] = null; }
        }
        const r = recs[p.id];
        if (r) {
          for (const it of r.items || []) {
            if (it.componentProductId) {
              const key = `p:${it.componentProductId}`;
              if (compStocks[key] === undefined) {
                try {
                  const s = await api.getLocationStocks(it.componentProductId);
                  compStocks[key] = (s.items || []).reduce((acc, x) => acc + (x.stock || 0), 0);
                } catch { compStocks[key] = 0; }
              }
            } else if (it.componentMaterialId) {
              const key = `m:${it.componentMaterialId}`;
              if (compStocks[key] === undefined) {
                const m = materialsMap[it.componentMaterialId];
                compStocks[key] = m ? m.currentStock : 0;
              }
            }
          }
        }
      }
      setRecipesByProduct(recs);
      setComponentTotalStock(compStocks);
    })();
  }, [topProducts, materialsMap]);

  const sparkPath = useMemo(() => {
    if (!series || series.length === 0) return '';
    const values = series.map(s => s.value || 0);
    const w = 320, h = 80, pad = 4;
    const max = Math.max(1, ...values);
    const min = Math.min(0, ...values);
    const scaleX = (i: number) => pad + (i * (w - 2 * pad)) / Math.max(1, values.length - 1);
    const scaleY = (v: number) => h - pad - ((v - min) / Math.max(1e-6, (max - min))) * (h - 2 * pad);
    return values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(v)}`).join(' ');
  }, [series]);

  // Compute procurement + manufacturing timeline (days) from recipe
  const timeline = useMemo(() => {
    if (!selectedRecipe) return null;
    const items = selectedRecipe.items || [];
    const procurementDays = Math.max(0, ...items.map(i => i.procurementLeadDays ?? 7));
    const inboundDays = 2; // simple shipping/receiving buffer
    const serviceHours = items.reduce((acc, it) => acc + ((it.componentServiceId && it.serviceProcessingHoursPerUnit) ? (it.serviceProcessingHoursPerUnit * (it.quantity || 0)) : 0), 0);
    const manufacturingHours = (selectedRecipe.manufacturingTimeHours || 0) + serviceHours;
    const serviceDays = Math.ceil(serviceHours / 8);
    const manufacturingDays = Math.ceil((selectedRecipe.manufacturingTimeHours || 0) / 8);
    const total = procurementDays + inboundDays + serviceDays + manufacturingDays;
    return { procurementDays, inboundDays, serviceDays, manufacturingDays, total };
  }, [selectedRecipe]);

  const hoursForWindow = useMemo(() => ({ '1h': 1, '2h': 2, '4h': 4, '8h': 8, 'day': 24 })[projectionWindow], [projectionWindow]);

  function computeReplenishment(prod: ProductType) {
    const rec = recipesByProduct[prod.id];
    const rDay = prod.daysOfStock > 0 ? (prod.currentStock || 0) / Math.max(1e-6, prod.daysOfStock) : 0; // units/day
    const rHour = rDay / 24; // units/hour
    const baseLead = (rec?.items || []).reduce((max, it) => Math.max(max, it.procurementLeadDays ?? 7), 0) + 2;
    const serviceHours = (rec?.items || []).reduce((acc, it) => acc + ((it.componentServiceId && it.serviceProcessingHoursPerUnit) ? (it.serviceProcessingHoursPerUnit * (it.quantity || 0)) : 0), 0);
    const leadDays = baseLead + Math.ceil(((rec?.manufacturingTimeHours || 0) + serviceHours) / 8);
    const leadHours = leadDays * 24;
    // Buildable units from current component stocks
    let buildable = Infinity;
    if (rec && rec.items && rec.items.length > 0) {
      for (const it of rec.items) {
        const key = it.componentProductId ? `p:${it.componentProductId}` : `m:${it.componentMaterialId}`;
        const have = componentTotalStock[key] ?? 0;
        const req = Math.max(1e-6, it.quantity || 0);
        buildable = Math.min(buildable, Math.floor(have / req));
      }
    } else {
      buildable = 0;
    }
    if (!isFinite(buildable)) buildable = 0;
    const totalUnitsCovered = (prod.currentStock || 0) + buildable;
    const timeToStockoutHours = rHour > 0 ? totalUnitsCovered / rHour : Infinity;
    const shouldOrderNow = timeToStockoutHours <= leadHours;
    return {
      rHour,
      rWindow: rHour * hoursForWindow,
      leadDays,
      timeToStockoutHours,
      buildable,
      shouldOrderNow,
    };
  }

  const kpiCritical = useMemo(() => (allProducts || []).filter(p => (p.currentStock || 0) <= 0).length, [allProducts]);
  const kpiAtRisk = useMemo(() => (allProducts || []).filter(p => typeof p.daysOfStock === 'number' ? (p.daysOfStock as number) < riskDays : (p.currentStock || 0) <= (p.reorderPoint || 0)).length, [allProducts, riskDays]);

  const avgTimeToStockoutDays = useMemo(() => {
    const list = (topProducts || []).map(p => computeReplenishment(p).timeToStockoutHours / 24).filter(v => isFinite(v) && v >= 0);
    if (!list.length) return null;
    const avg = list.reduce((a, b) => a + b, 0) / list.length;
    return avg;
  }, [topProducts]);

  const top3AtRisk = useMemo(() => {
    const arr = (allProducts || [])
      .map(p => ({ p, doh: typeof p.daysOfStock === 'number' ? p.daysOfStock as number : Infinity }))
      .filter(x => isFinite(x.doh))
      .sort((a, b) => a.doh - b.doh)
      .slice(0, 3);
    return arr;
  }, [allProducts]);

  const handleViewProduct = (id: number) => {
    // Navigate to product detail
    console.log('View product:', id);
  };

  const handleEditProduct = (id: number) => {
    // Navigate to product edit
    console.log('Edit product:', id);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Cargando dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {/* Header: saludo + buscador global */}
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight={700}>Hola {user?.name},</Typography>
            <Typography variant="body2" color="text.secondary">Esto es el estado de tu inventario hoy</Typography>
          </Box>
          <Box sx={{ minWidth: 320, width: { xs: '100%', md: 420 } }}>
            <TextField fullWidth placeholder="Buscar SKU, producto o proveedor" InputProps={{ startAdornment: (<InputAdornment position="start"><Search /></InputAdornment>) }} />
          </Box>
        </Stack>
      </Box>

      {/* KPIs principales - misma altura y grid auto-fit */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: sectionGap, mb: 4 }}>
        <StatCard
          title="Inventario Valorizado"
          value={`$${(stats?.totalInventoryValue ?? 0).toLocaleString()}`}
          icon={<AttachMoney fontSize="small" />}
          color="primary.main"
          onClick={() => navigate('/inventory')}
          tooltip="Suma de (costo unitario × stock actual) de todos los SKUs. No incluye impuestos ni fletes. Se actualiza con cada movimiento."
          
        />
        <StatCard
          title="Stocks Críticos"
          value={kpiCritical}
          icon={<Error fontSize="small" />}
          color="#ef4444"
          onClick={() => navigate('/analytics')}
          tooltip="Cantidad de SKUs con stock disponible igual a 0 en todos los almacenes."
        />
        <StatCard
          title={`En riesgo (días de cobertura < ${riskDays})`}
          value={kpiAtRisk}
          icon={<Warning fontSize="small" />}
          color="#f59e0b"
          onClick={() => navigate('/replenishment')}
          tooltip="Productos cuyo indicador 'días de cobertura' (stock dividido por demanda diaria proyectada) es menor al umbral seleccionado."
        />
        <StatCard
          title="Ganancia neta (período)"
          value={`$${Math.round(Number(profit?.totals.netProfit ?? 0)).toLocaleString('es-AR')}`}
          icon={<AttachMoney fontSize="small" />}
          color={(profit?.totals.netProfit ?? 0) < 0 ? '#ef4444' : '#0f766e'}
          onClick={() => navigate('/profitability')}
          tooltip="Ventas netas de IVA − costo directo (receta y comisiones del canal) − gastos fijos del período. Detalle por producto en Rentabilidad."
        />
      </Box>

      {/* Selector de período y acciones */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">Período:</Typography>
          <Button size="small" variant={kpiPeriod==='day'?'contained':'outlined'} onClick={() => setKpiPeriod('day')}>Día</Button>
          <Button size="small" variant={kpiPeriod==='week'?'contained':'outlined'} onClick={() => setKpiPeriod('week')}>Semana</Button>
          <Button size="small" variant={kpiPeriod==='month'?'contained':'outlined'} onClick={() => setKpiPeriod('month')}>Mes</Button>
          <Button size="small" variant={kpiPeriod==='custom'?'contained':'outlined'} onClick={() => setKpiPeriod('custom')}>Custom</Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {kpiPeriod === 'custom' && (
            <>
              <TextField
                type="date"
                size="small"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <TextField
                type="date"
                size="small"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </>
          )}
          <Button size="small" variant="outlined" onClick={() => {
            // Export simple de KPIs a CSV
            const rows = [
              ['periodo','ventas','ordenes','ticket_promedio','tendencia_pct','unidades'],
              [kpiPeriod, String(kpis?.totalSales ?? 0), String(kpis?.totalOrders ?? 0), String(kpis?.avgOrderValue ?? 0), String(kpis?.salesTrendPct ?? 0), String(kpis?.totalUnits ?? 0)]
            ];
            const csv = rows.map(r => r.join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `dashboard-${kpiPeriod}.csv`; a.click(); URL.revokeObjectURL(url);
          }}>Exportar métricas</Button>
        </Stack>
      </Stack>

      {profit && <ProfitSummary data={profit} onOpen={() => navigate('/profitability')} />}

      {/* Métricas secundarias */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: { xs: 2, md: 3 }, mb: 4 }}>
        <Card sx={{ height: '100%' }}>
          <CardActionArea onClick={() => navigate('/analytics')} sx={{ height: '100%' }}>
            <CardContent>
              <Tooltip title="Ventas netas en el período seleccionado (sin cancelaciones)." arrow><Typography variant="body2" color="text.secondary">Ventas ({kpiPeriod})</Typography></Tooltip>
              <Typography variant="h4">${(kpis?.totalSales ?? 0).toLocaleString()}</Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardActionArea onClick={() => navigate('/analytics')} sx={{ height: '100%' }}>
            <CardContent>
              <Tooltip title="Unidades totales vendidas en el período seleccionado (todas las fuentes)." arrow><Typography variant="body2" color="text.secondary">Unidades vendidas</Typography></Tooltip>
              <Typography variant="h4">{(kpis?.totalUnits ?? 0).toLocaleString()}</Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardActionArea onClick={() => navigate('/purchases')} sx={{ height: '100%' }}>
            <CardContent>
              <Tooltip title="Órdenes confirmadas en el período. Incluye e‑commerce y retail si están integrados." arrow><Typography variant="body2" color="text.secondary">Órdenes</Typography></Tooltip>
              <Typography variant="h4">{kpis?.totalOrders ?? 0}</Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardActionArea onClick={() => navigate('/analytics')} sx={{ height: '100%' }}>
            <CardContent>
              <Tooltip title="Ventas / Órdenes del período (AR$)." arrow><Typography variant="body2" color="text.secondary">Ticket promedio</Typography></Tooltip>
              <Typography variant="h4">${Math.round(kpis?.avgOrderValue ?? 0).toLocaleString()}</Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardActionArea onClick={() => navigate('/analytics')} sx={{ height: '100%' }}>
            <CardContent>
              <Tooltip title="Variación porcentual vs período anterior equivalente." arrow><Typography variant="body2" color="text.secondary">Tendencia</Typography></Tooltip>
              <Typography variant="h4" color={(kpis?.salesTrendPct ?? 0) >= 0 ? 'success.main' : 'error.main'}>{(kpis?.salesTrendPct ?? 0)}%</Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>

      {/* Ventas por canal */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Ventas por canal</Typography>
            <Typography variant="body2" color="text.secondary">
              Ventas brutas, órdenes y unidades por canal en el período seleccionado.
            </Typography>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Canal</TableCell>
                  <TableCell align="right">Ventas brutas</TableCell>
                  <TableCell align="right">Órdenes</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(salesByChannel || []).map((row) => (
                  <TableRow key={row.channel}>
                    <TableCell>{row.channel}</TableCell>
                    <TableCell align="right">${(row.totalSales ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{row.totalOrders ?? 0}</TableCell>
                    <TableCell align="right">{row.totalUnits ?? 0}</TableCell>
                  </TableRow>
                ))}
                {(!salesByChannel || salesByChannel.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">No hay ventas registradas en el período seleccionado.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Sales Forecast Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Pronóstico de ventas</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ventas actuales + proyectadas basadas en oportunidades (próximos 3 meses)
          </Typography>
          {salesForecast && salesForecast.months.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {salesForecast.months.map((m, idx) => (
                <Card key={m.month} variant="outlined" sx={{ flex: '1 1 200px', minWidth: 200 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">{m.label}</Typography>
                    <Typography variant="h5" fontWeight={600}>${m.actualSales.toLocaleString()}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Órdenes:</Typography>
                        <Typography variant="caption">{m.orderCount}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Oportunidades:</Typography>
                        <Typography variant="caption">{m.opportunityCount}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Proyectado:</Typography>
                        <Typography variant="caption" color="primary.main">${m.expectedValue.toLocaleString()}</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">Sin datos de pronóstico.</Typography>
          )}
        </CardContent>
      </Card>

      {/* YoY Comparison and Quarterly Breakdown - side by side */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: sectionGap, mb: 4 }}>
        {/* YoY Comparison Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Comparación año vs año</Typography>
            {yoyComparison && yoyComparison.months.length > 0 ? (
              <>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Chip
                    label={`${yoyComparison.currentYear}: $${yoyComparison.currentYearTotal.toLocaleString()}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`${yoyComparison.previousYear}: $${yoyComparison.previousYearTotal.toLocaleString()}`}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    icon={yoyComparison.overallChangePercentage >= 0 ? <TrendingUp /> : <TrendingDown />}
                    label={`${yoyComparison.overallChangePercentage >= 0 ? '+' : ''}${yoyComparison.overallChangePercentage}%`}
                    color={yoyComparison.overallChangePercentage >= 0 ? 'success' : 'error'}
                    size="small"
                  />
                </Stack>
                <TableContainer sx={{ maxHeight: 250 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mes</TableCell>
                        <TableCell align="right">{yoyComparison.currentYear}</TableCell>
                        <TableCell align="right">{yoyComparison.previousYear}</TableCell>
                        <TableCell align="right">Cambio</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {yoyComparison.months.map((m) => (
                        <TableRow key={m.month}>
                          <TableCell>{m.monthName}</TableCell>
                          <TableCell align="right">${m.currentYearSales.toLocaleString()}</TableCell>
                          <TableCell align="right">${m.previousYearSales.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={m.changePercentage >= 0 ? 'success.main' : 'error.main'}
                            >
                              {m.changePercentage >= 0 ? '+' : ''}{m.changePercentage}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography color="text.secondary">Sin datos de comparación.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Quarterly Breakdown Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Desglose trimestral {quarterlyBreakdown?.year || new Date().getFullYear()}
            </Typography>
            {quarterlyBreakdown && quarterlyBreakdown.quarters.length > 0 ? (
              <>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Chip
                    label={`Total: $${quarterlyBreakdown.yearTotal.toLocaleString()}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`${quarterlyBreakdown.yearOrderCount} órdenes`}
                    variant="outlined"
                    size="small"
                  />
                </Stack>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                  {quarterlyBreakdown.quarters.map((q) => {
                    const maxSales = Math.max(...quarterlyBreakdown.quarters.map(x => x.totalSales)) || 1;
                    const heightPct = (q.totalSales / maxSales) * 100;
                    return (
                      <Box key={q.quarter} sx={{ flex: 1, textAlign: 'center' }}>
                        <Box
                          sx={{
                            height: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: '80%',
                              height: `${Math.max(heightPct, 5)}%`,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                              transition: 'height 0.3s',
                            }}
                          />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>{q.label}</Typography>
                        <Typography variant="body2">${q.totalSales.toLocaleString()}</Typography>
                        <Typography variant="caption" color="text.secondary">{q.orderCount} órdenes</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Typography color="text.secondary">Sin datos trimestrales.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Fason vs Regular and Strategic Client Report - side by side */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: sectionGap, mb: 4 }}>
        {/* Fason vs Regular Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Fasón vs Regular</Typography>
            {salesByCustomerType ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Box sx={{ position: 'relative', width: 150, height: 150 }}>
                    <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="20"
                      />
                      {/* Fason segment */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#8b5cf6"
                        strokeWidth="20"
                        strokeDasharray={`${(salesByCustomerType.fasonPercentage / 100) * 251.2} 251.2`}
                      />
                    </svg>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h5" fontWeight={700}>{salesByCustomerType.fasonPercentage}%</Typography>
                      <Typography variant="caption" color="text.secondary">Fasón</Typography>
                    </Box>
                  </Box>
                </Box>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Circle sx={{ color: '#8b5cf6', fontSize: 12 }} />
                      <Typography variant="body2">Fasón</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>${salesByCustomerType.fasonSales.toLocaleString()}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Circle sx={{ color: '#e0e0e0', fontSize: 12 }} />
                      <Typography variant="body2">Regular</Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>${salesByCustomerType.regularSales.toLocaleString()}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600}>Total</Typography>
                    <Typography variant="body2" fontWeight={600}>${salesByCustomerType.totalSales.toLocaleString()}</Typography>
                  </Stack>
                </Stack>
              </>
            ) : (
              <Typography color="text.secondary">Sin datos de tipo de cliente.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Strategic Client Report */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Reporte de cliente estratégico</Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Seleccionar cliente"
              value={selectedStrategicCustomer || ''}
              onChange={(e) => setSelectedStrategicCustomer(e.target.value ? Number(e.target.value) : null)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">
                <em>Seleccionar cliente...</em>
              </MenuItem>
              {strategicCustomers.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.legalName}</MenuItem>
              ))}
            </TextField>
            {strategicReport && strategicReport.clients.length > 0 ? (
              <>
                {strategicReport.clients.map((client) => (
                  <Box key={client.customerId}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">{client.customerName}</Typography>
                      <Chip label={client.customerType || 'Sin tipo'} size="small" variant="outlined" />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Pagado</Typography>
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          ${client.totalPaid.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Pendiente</Typography>
                        <Typography variant="body2" color="warning.main" fontWeight={600}>
                          ${client.totalPending.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Proyectado</Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          ${client.totalProjected.toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Línea de tiempo</Typography>
                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Estado</TableCell>
                            <TableCell>Ref</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell align="right">Monto</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {client.timeline.slice(0, 10).map((entry) => {
                            const colorMap: Record<string, string> = {
                              green: '#22c55e',
                              yellow: '#eab308',
                              gray: '#9ca3af',
                              violet: '#8b5cf6',
                            };
                            return (
                              <TableRow key={`${entry.entryType}-${entry.id}`}>
                                <TableCell>
                                  <Circle sx={{ color: colorMap[entry.color] || '#9ca3af', fontSize: 12, mr: 0.5 }} />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">{entry.reference}</Typography>
                                  {entry.hasPendingCheck && (
                                    <Chip label="Cheque" size="small" sx={{ ml: 0.5, bgcolor: '#8b5cf6', color: 'white' }} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">{new Date(entry.date).toLocaleDateString()}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption">${entry.amount.toLocaleString()}</Typography>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Circle sx={{ color: '#22c55e', fontSize: 10 }} />
                        <Typography variant="caption">Pagado</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Circle sx={{ color: '#eab308', fontSize: 10 }} />
                        <Typography variant="caption">Pendiente</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Circle sx={{ color: '#9ca3af', fontSize: 10 }} />
                        <Typography variant="caption">Proyectado</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Circle sx={{ color: '#8b5cf6', fontSize: 10 }} />
                        <Typography variant="caption">Cheque pendiente</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </>
            ) : selectedStrategicCustomer ? (
              <Typography color="text.secondary">Cargando datos del cliente...</Typography>
            ) : (
              <Typography color="text.secondary">Seleccioná un cliente para ver su reporte.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Gráfico predictivo (fila completa) con mini-widget */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
            <Tooltip title="Curva de ventas proyectadas vs stock disponible. Zonas: verde (seguro), amarillo (reorden), rojo (quiebre). La línea vertical marca el tiempo total (compra+fabricación)." arrow>
              <Typography variant="h6" fontWeight={600}>Proyección ventas vs stock</Typography>
            </Tooltip>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">Producto:</Typography>
              <select value={selectedProductForTimeline ?? ''} onChange={e => setSelectedProductForTimeline(e.target.value ? Number(e.target.value) : null)} style={{ padding: 8, borderRadius: 8, border: '1px solid rgba(0,0,0,0.2)' }}>
                {(allProducts || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Horizonte:</Typography>
              <select value={projectionWindow} onChange={e => setProjectionWindow(e.target.value as any)} style={{ padding: 8, borderRadius: 8, border: '1px solid rgba(0,0,0,0.2)' }}>
                <option value="1h">1h</option>
                <option value="2h">2h</option>
                <option value="4h">4h</option>
                <option value="8h">8h</option>
                <option value="day">Día</option>
              </select>
            </Stack>
          </Stack>
          {avgTimeToStockoutDays !== null && (
            <Box sx={{ mb: 1.5 }}>
              <Chip label={`Tiempo promedio hasta quiebre: ${avgTimeToStockoutDays.toFixed(1)} días`} color={avgTimeToStockoutDays > 3 ? 'success' : avgTimeToStockoutDays > 1 ? 'warning' : 'error'} />
            </Box>
          )}
          {(() => {
            const p = (allProducts || []).find(x => x.id === selectedProductForTimeline);
            if (!p) return <Typography variant="body2" color="text.secondary">Seleccioná un producto.</Typography>;
            const m = computeReplenishment(p);
            return (
              <ProjectionChart step={projectionWindow} horizonDays={14} stockStart={p.currentStock || 0} buildableUnits={m.buildable} salesRatePerHour={m.rHour} leadDays={m.leadDays} />
            );
          })()}
          {timeline && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Pipeline de producción</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Chip label={`Procura: ${timeline.procurementDays} d`} />
                <Chip label={`Inbound: ${timeline.inboundDays} d`} />
                {typeof timeline.serviceDays === 'number' && <Chip label={`Servicios: ${timeline.serviceDays} d`} />}
                <Chip label={`Fabricación: ${timeline.manufacturingDays} d`} />
                <Chip color="primary" label={`Total: ${timeline.total} d`} />
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Reabastecimiento inteligente */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Reabastecimiento inteligente</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">Vista:</Typography>
              <Button size="small" variant={simpleMode ? 'contained' : 'outlined'} onClick={() => setSimpleMode(true)}>Básica</Button>
              <Button size="small" variant={!simpleMode ? 'contained' : 'outlined'} onClick={() => setSimpleMode(false)}>Tabla</Button>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Ritmo proyectado:</Typography>
              <select value={projectionWindow} onChange={e => setProjectionWindow(e.target.value as any)} style={{ padding: 8, borderRadius: 8, border: '1px solid rgba(0,0,0,0.2)' }}>
                <option value="1h">1h</option>
                <option value="2h">2h</option>
                <option value="4h">4h</option>
                <option value="8h">8h</option>
                <option value="day">Día</option>
              </select>
            </Stack>
          </Stack>
          {simpleMode ? (
          <Stack spacing={2}>
            {topProducts.map(p => {
              const rec = recipesByProduct[p.id] || null;
              const m = computeReplenishment(p);
              const daysToStockout = isFinite(m.timeToStockoutHours) ? (m.timeToStockoutHours / 24) : Infinity;
              const status: 'red'|'yellow'|'green' = m.shouldOrderNow ? 'red' : (daysToStockout <= m.leadDays * 2 ? 'yellow' : 'green');
              const statusColor = status==='red' ? 'error.main' : status==='yellow' ? 'warning.main' : 'success.main';
              return (
                <Box key={p.id} sx={{ border: '1px solid', borderColor: 'divider', p: 1.5, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="subtitle2">{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Stock: {p.currentStock} • Construible: {m.buildable}</Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <TrafficLight state={status} />
                        <BlockMeter value={Math.min(m.timeToStockoutHours, m.leadDays * 24)} max={m.leadDays * 24} color={status==='red' ? '#ef4444' : status==='yellow' ? '#f59e0b' : '#22c55e'} />
                        <Typography variant="body2" color={statusColor}>⚠️ Te quedás sin stock en {isFinite(daysToStockout) ? daysToStockout.toFixed(1) + ' d' : '—'}</Typography>
                      </Stack>
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant={m.shouldOrderNow ? 'contained' : 'outlined'}
                          color={m.shouldOrderNow ? 'error' : 'primary'}
                          onClick={() => navigate('/purchases', {
                            state: { productId: p.id, suggestedQty: Math.ceil(m.rWindow * m.leadDays), materials: rec?.items }
                          })}
                        >
                          Orden de compra
                        </Button>
                        {rec ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate('/production-orders', {
                              state: { productId: p.id, recipeId: rec?.id, suggestedQty: m.buildable }
                            })}
                          >
                            Orden de fabricación
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            onClick={() => navigate(`/recipes?new=1&productId=${p.id}`)}
                          >
                            + Formulación
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
            {topProducts.length === 0 && (
              <Typography color="text.secondary">No hay productos para calcular.</Typography>
            )}
          </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Stock</TableCell>
                    <TableCell align="right">Fabricable</TableCell>
                    <TableCell align="right">Días hasta quiebre</TableCell>
                            <TableCell align="right">Tiempo total (días)</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProducts.map(p => {
                    const rec = recipesByProduct[p.id] || null;
                    const m = computeReplenishment(p);
                    const daysToStockout = isFinite(m.timeToStockoutHours) ? (m.timeToStockoutHours / 24) : Infinity;
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.name}</TableCell>
                        <TableCell align="right">{p.currentStock || 0}</TableCell>
                        <TableCell align="right">{m.buildable}</TableCell>
                        <TableCell align="right">{isFinite(daysToStockout) ? daysToStockout.toFixed(1) : '—'}</TableCell>
                        <TableCell align="right">{m.leadDays}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => navigate('/purchases', {
                                state: { productId: p.id, suggestedQty: Math.ceil(m.rWindow * m.leadDays) }
                              })}
                            >
                              Compra
                            </Button>
                            {rec ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate('/production-orders', {
                                  state: { productId: p.id, recipeId: rec.id, suggestedQty: m.buildable }
                                })}
                              >
                                Fabricar
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                onClick={() => navigate(`/recipes?new=1&productId=${p.id}`)}
                              >
                                + Formulación
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Alertas y Movimientos */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: sectionGap }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Tooltip title="SKUs que requieren acción para evitar quiebre de stock." arrow>
                <Typography variant="h6" fontWeight={600}>Alertas de stock</Typography>
              </Tooltip>
              <Button size="small" variant="outlined" onClick={() => navigate('/replenishment')}>Ver todo</Button>
            </Stack>
            <List sx={{ maxHeight: 320, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {(stats?.lowStockAlert || []).slice(0, 8).map((p: any) => {
                const doh = typeof p.daysOfStock === 'number' ? p.daysOfStock : undefined;
                const suggestion = (purchaseSuggestions || []).find((s: any) => s.productId === p.id);
                return (
                  <ListItem key={p.id} sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <ListItemText
                      primary={`${p.name} (${p.sku})`}
                      secondary={
                        <Stack component="span" spacing={0.5}>
                          <span>{doh !== undefined ? `${Number(doh).toFixed(1)} días de stock` : `Stock: ${p.currentStock}`}</span>
                          {suggestion && <span style={{ color: '#1976d2' }}>Sugerido: {suggestion.suggestedQty} u • {suggestion.supplierName || 'Sin proveedor'}</span>}
                        </Stack>
                      }
                      sx={{ minWidth: 0, flex: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      color={doh !== undefined && doh < 3 ? 'error' : 'primary'}
                      sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                      onClick={() => navigate('/purchases', {
                        state: {
                          productId: p.id,
                          suggestedQty: suggestion?.suggestedQty,
                          supplierId: suggestion?.supplierId
                        }
                      })}
                    >
                      Reponer
                    </Button>
                  </ListItem>
                );
              })}
              {(!stats?.lowStockAlert || stats.lowStockAlert.length === 0) && (
                <ListItem><ListItemText primary="Sin alertas de stock" secondary="Todos los productos tienen stock suficiente" /></ListItem>
              )}
            </List>
          </CardContent>
        </Card>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Tooltip title="Movimientos de inventario más recientes (ingresos, egresos, ajustes)." arrow>
              <Typography variant="h6" fontWeight={600} gutterBottom>Movimientos recientes</Typography>
            </Tooltip>
            <List sx={{ maxHeight: 320, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {(recentMovs || []).map((m: any, idx: number) => (
                <ListItem key={idx}><ListItemText primary={`${m.type} ${m.quantity} • ${m.reason}`} secondary={new Date(m.createdAt).toLocaleString()} /></ListItem>
              ))}
              {(recentMovs || []).length === 0 && (
                <ListItem><ListItemText primary="Sin movimientos" /></ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
